export interface BookCharacter {
  name: string;
  alias: string;
  role: string;
  color: string;
  description: string;
}

export interface BookSection {
  id: string;
  number: string;
  title: string;
  subChapters: string[];
  content: string;
}

export const bookTitle = "The Wraith";
export const bookSubtitle = "A Story of What Remains";
export const bookTagline =
  "After the world ended, two people tried to cook their way back to something worth surviving for.";

export const bookCoverImage =
  "https://public.readdy.ai/ai/img_res/5bef2294dba9f243e595ebdc9c79ccae.jpg";

export const characters: BookCharacter[] = [
  {
    name: "Jordan Reyes",
    alias: "The Wraith",
    role: "Protagonist",
    color: "#C8922A",
    description:
      "Former Division I pitcher. Before the bat was wrapped in barbed wire, it won championships. She carries her mother's voice in her chest like armor — Build something better, mija — and she has not stopped building since the world gave her rubble to work with.",
  },
  {
    name: "Aidan Lee",
    alias: "The Chef",
    role: "Protagonist",
    color: "#7FAF6A",
    description:
      "Culinary school misfit. Ran The Wandering Skillet, a food truck that served elevated comfort food and, secretly, proof that a stranger could be cared for. He cooks for the same reason he always did: because someone is always hungry, and hunger is the one language everyone speaks.",
  },
  {
    name: "Ruth",
    alias: "The Keeper",
    role: "Survivor — Millhaven",
    color: "#A07850",
    description:
      "Sixty-three years old. Former schoolteacher. Runs the Millhaven community with a combination of quiet authority, impeccable organization, and the terrifying patience of someone who has already survived the worst thing that could happen to her and decided to keep going anyway.",
  },
  {
    name: "Nina",
    alias: "",
    role: "Survivor — Millhaven",
    color: "#9A9A9A",
    description:
      "Nine years old. Doesn't speak much. Keeps a notebook of drawings — maps of places she's been, faces of people she's met, plants that are safe to eat. She gave Aidan one drawing. It was a grilled cheese sandwich. He keeps it folded in his front pocket.",
  },
  {
    name: "Elena Reyes",
    alias: "Mom",
    role: "Jordan's Mother",
    color: "#B07090",
    description:
      "Registered nurse. Twenty-three years of night shifts and a lower back that never got better and a daughter she built her whole hope around. She is the reason Jordan is still moving. She does not know this. Jordan has not found a way to tell her.",
  },
];

export const bookSections: BookSection[] = [
  {
    id: "prologue",
    number: "PRO",
    title: "Prologue: Before the Batter",
    subChapters: ["The Last Normal Day", "The Massacre", "The Long Winter"],
    content: `!! The world was still whole. The sky was blue. This matters.

## The Last Normal Day

A softball arcs through the air in a perfect, punishing line. It smacks into a catcher's mitt with a sound like a gunshot.

Strike Three rings out from the umpire.

JORDAN REYES stands on the mound. Hair pulled back, cleats in the grass, arm still extended from the follow-through. She has the focused stillness of someone who has practiced a single motion until it becomes a reflex.

Her coach nods from the sideline. That's enough. That's everything.

Jordan rolls her shoulder, reaching for her water bottle. It's then she notices the smell drifting across the field. Something good. Something warm and completely out of place.

She turns.

At the edge of the athletic complex: a bright yellow food truck. Hand-painted letters on the side, slightly uneven. The sign reads: THE WANDERING SKILLET — "Elevated Comfort Food for the Undeserving Masses."

The small crowd around the window suggests the food is worth the poetry.

Jordan is not in the habit of detours. She picks up her bat bag. She walks straight toward the locker room. She stops. She turns around and gets in line.

## Aidan at The Wandering Skillet

AIDAN LEE looks up from a pan that is doing several things at once under his hands, which is apparently fine with him. He's got flour somewhere on his face that he hasn't noticed. He has the look of someone who is precisely where they are supposed to be.

SPEAKER:AIDAN
> "Hey! What can I get you?"

SPEAKER:JORDAN (studying the menu board)
> "What's the 'Morale-Boosting Mac & Cheese'?"

SPEAKER:AIDAN
> "It's mac and cheese. But I put a very encouraging label on it."

SPEAKER:JORDAN
> "That's the whole gimmick?"

SPEAKER:AIDAN
> "The gimmick is that it's the best mac and cheese you've ever had. And when your day is bad — which I'm guessing yours sometimes is, you've got that look — you'll remember the name and it'll work retroactively as therapy. I'm thinking long-term here."

SPEAKER:JORDAN
> "What look?"

SPEAKER:AIDAN
> "The 'I'm carrying something heavy and I don't put it down' look. First one's free. Consider it market research."

She takes the bowl. She takes one bite. She makes a sound entirely against her will. Aidan grins.

SPEAKER:JORDAN (firmly)
> "It's fine."

SPEAKER:AIDAN
> "You made a noise."

SPEAKER:JORDAN
> "I didn't."

And then Jordan snorts. A genuine, undignified, helpless snort of laughter. She claps a hand over her face. It makes it worse. Aidan looks at her like she just answered a question he didn't know he was asking.

SPEAKER:AIDAN (quietly — more sincere than anything he's said so far)
> "I'm Aidan."

SPEAKER:JORDAN
> "Jordan."

SPEAKER:AIDAN
> "Come back tomorrow. I'm doing a short rib situation that'll ruin other food for you forever."

SPEAKER:JORDAN (walking away, not looking back)
> "Bold claim."

SPEAKER:AIDAN (calling after her)
> "It's a promise, actually!"

She doesn't turn around. But she smiles, facing away from him, where he can't see it. She comes back the next day. And the day after that.

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/d8043c39-1f36-49ca-b1e8-bf945c6c24b3_Aidan-in-the-Wandering-Skillet.png?v=8b8dc028a5bccd810f5334b62c4354b5]]

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/acb4c7c9-0ac7-42c0-8ffe-7bd6ae1b1211_Jordan-first-visits-The-Wandering-Skillet.png?v=af6f8641edb5bdac433fc0e394e5e8a9]]

## Montage — The Year Before

Jordan sitting on the food truck's back bumper while Aidan scrubs the griddle, both laughing at something.

Aidan at one of Jordan's games, in the stands, absolutely not understanding softball but cheering at the right moments because he's been studying her face to know when.

Late night. Jordan asleep on a couch, still in practice gear. Aidan has draped a blanket over her without waking her and is doing the dishes quietly.

Aidan's food truck, closed for the night. They're sitting on the roof, shoulders touching, looking at the campus lit up below. No dialogue. Just the picture of two people who have stopped pretending they're only friends.

## Green Monday

!! ⚠ EMERGENCY ALERT — GRAY FEVER CRITICAL ESCALATION · SHELTER IN PLACE — AVOID ALL CONTACT WITH SYMPTOMATIC INDIVIDUALS · THIS IS NOT A DRILL.

Every phone in the room goes off at the same moment. The sound is a specific kind of alarm — the Emergency Alert System, reserved for things that cannot be said calmly. The screen of every phone reads in a green banner that gives the day its name.

The room goes from lecture-quiet to panic-loud in under four seconds. Jordan is already on her feet. Bat in hand. Eyes moving to every exit.

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/2b8063ad-1cc9-4118-9d63-f8958093566e_every-phone-goes-off-at-once.png?v=d4cf83d39d2fb9c898fa59dc541b238e]]

Outside is wrong. Everything is wrong all at once, in the way that catastrophe moves — not a single terrible thing but a dozen ordinary things failing simultaneously. People run with no direction because the danger is everywhere and nowhere.

Jordan moves against the current of the crowd. Her phone rings.

SPEAKER:AIDAN (urgent but controlled)
> "Jordan. Where are you?"

SPEAKER:JORDAN
> "North quad. You?"

SPEAKER:AIDAN
> "Kitchen classroom building, second floor. I've got three people with me. Don't go near the student center. Just don't."

SPEAKER:JORDAN
> "Stay there. I'm coming to you."

## The Culinary Classroom

Aidan has, in the twenty minutes before Jordan arrived: pushed heavy prep tables against the door, inventoried every item in the room, filled every container with water before the pressure dropped, and organized four terrified people into something resembling function by giving them each a specific task.

When Jordan gets through the door, he crosses the room and puts his arms around her, and she grips the back of his jacket with her free hand — the last purely normal moment either of them will have for a very long time.

SPEAKER:AIDAN (into her hair)
> "I counted your building twice from the window. You weren't there."

SPEAKER:JORDAN
> "I was in lecture. I'm here."

SPEAKER:AIDAN
> "You're here."

He pulls back. His eyes move over her the way a chef checks a finished dish — quickly, thoroughly, looking for anything wrong.

SPEAKER:JORDAN
> "We need to move. My car's in the south lot. We can get to my mom's apartment—"

SPEAKER:AIDAN
> "Service corridor behind the kitchen connects to the utilities hall, which runs under the east side of campus. We come up behind the facilities building — right at the edge of the south lot. Less exposure."

SPEAKER:JORDAN
> "How do you know the utilities corridor?"

SPEAKER:AIDAN
> "I had a very good reason to sneak around this campus that has nothing to do with the current situation. I'll explain later."

She looks at him. He has, somehow, found a cast-iron skillet and is holding it with the easy grip of someone who has been carrying it his whole life.

SPEAKER:JORDAN
> "Where did you get that?"

SPEAKER:AIDAN
> "It's mine. I carry it to class. For demonstrations. It's a very important pan, Jordan."

She turns to the door and starts moving.

SPEAKER:JORDAN (quietly)
> "That's the most you thing I've ever heard."

## Elena's Apartment

Empty. Intact. No signs of violence. Just suddenly, completely, vacated.

Jordan stands in the kitchen. On the refrigerator, held by a magnet that says WORLD'S OKAYEST MOM — a gift Jordan bought at age nine, kept forever — a note.

The note reads: "Gone to Maria's. The girls are with me. Be safe my love. -Mom"

Jordan folds the note and puts it in her jacket pocket. Aidan is already opening cabinets.

SPEAKER:AIDAN
> "Your mom keeps a good pantry."

SPEAKER:JORDAN
> "She lived through hard times. She always kept a good pantry."

He pulls out a pot and moves to the stove. Jordan pulls a chair from the table and sits, back to the wall, bat across her knees, watching the street through the window. On watch. Letting him do this thing.

SPEAKER:AIDAN (without looking up)
> "Thank you."

SPEAKER:JORDAN
> "For what?"

SPEAKER:AIDAN
> "For not saying this is ridiculous."

SPEAKER:JORDAN
> "It is ridiculous."

SPEAKER:AIDAN
> "But you're still sitting down."

SPEAKER:JORDAN (softly)
> "She makes that for birthdays."

SPEAKER:AIDAN
> "Then consider this a new birthday. The old world ended today. Might as well have a cake."

## The Supermarket

Forty-three people. Jordan runs the scavenging teams. She's good at it. She's fast, she's quiet, and she loses nobody.

Aidan runs the kitchen area — a bank of camp stoves running off a generator. He learns what people need before they know to ask. He finds out who can't eat beans due to medication reactions. He learns that the children need the food that tastes most like before.

Every evening around the stoves, people gather. Not just for the food. For Aidan's commentary on the food, which is the first time most of them have laughed since Green Monday.

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/679d2674-7a6c-48ab-b363-0235e61af545_life-inside-the-fortified-supermarket.png?v=c973ff40b729c4e0035274a067dc749e]]

SPEAKER:AIDAN (ladling stew)
> "Tonight we have a limited-time offering I'm calling 'Mystery Protein Surprise.' Is it chicken? Is it the concept of chicken? We explore these questions together. As a community."

Groans, laughter, the specific warmth of people choosing to be human together despite everything.

Jordan stands at the edge of these gatherings, bat at her side, watching. She doesn't quite join in — she's always partly on watch, always counting exits. But she watches Aidan in the lamplight, doing the invisible labor of keeping forty-three humans tethered, and something in her face is unguarded and absolutely certain.

SPEAKER:AIDAN (looking at her)
> "I love you."

He says it the same way he plates food — deliberately, without fuss, because it's the right thing and the timing is right.

SPEAKER:JORDAN
> "I love you too. Obviously."

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/8e50a178-6788-4c9a-8889-76812f8ba053_Jordan-and-Aidan-finally-a-rest.png?v=c1e1ee1b898d77c95e326dbee2b90126]]

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/cb3bbab2-dabb-4ff8-977c-acb516f9ac4f_openart-image_1774834195948_813a1d50_1774834196002_693c1aee.png?v=c1e1ee1b898d77c95e326dbee2b90126]]

SPEAKER:AIDAN
> "Obviously."

SPEAKER:JORDAN
> "You've been making me dinner almost every day for two years. I think the position was clear."

## The Massacre

Shot fragmented — harsh light, movement, noise. The horde pouring in through three directions. Forty-three people waking up to the worst night any of them will survive.

Jordan fights toward Aidan through the panicked crowd. He's visible — twenty feet, then fifteen — then a child screams. It cuts through everything differently.

A small girl, maybe five, frozen against a display case as the crowd surges past her.

Jordan sees Aidan see it. She knows what he's going to do before he does it.

SPEAKER:JORDAN (screaming across the noise)
> "AIDAN—"

He's already going. He drops low and cuts sideways through the crowd toward the girl, against the grain of the panic, scooping her up in one arm, skillet in the other hand.

But the crowd physics are brutal and absolute. A wave of running people hits him from the left. Jordan fights against the same current. She can see his face—

SPEAKER:AIDAN (over the noise, caught in the surge)
> "JORDAN—"

The crowd drives them apart like water around stone. A fire door bangs open. Aidan is in that current, the girl in his arms, being carried toward the exit whether he chooses it or not.

He slams his hand against the fire door, screaming her name. She's fighting toward him — the door closes. The lock engages. The sound is final.

Jordan hits the door with both hands. It doesn't give.

From the other side, muffled but clear — one more time. His voice. Then the horde closes in around her and she has to move or die, and her body chooses what her mind can't.

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/4c1616f4-59c9-4ec8-aa47-5f9e6a90330f_fist-pounding-desperation-take-2.png?v=6f1a7b803edef5d8079da2e969028fc6]]

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/63bcbc66-2374-4b00-ae49-19b640aa823b_i-am-coming-for-you.png?v=6d4ef14f6ef0118eb39ed8a8d251a7a8]]

!! Jordan — I Am Coming For You.

## The Long Winter

## Jordan's Thread

— Alone. Moving constantly. Harder, quieter, more efficient. The bat acquires its barbed wire — she wraps it herself, methodically, in a dark warehouse. A transformation in the object that mirrors what's happening in her.

— Survivors tell each other stories about a girl who moves without sound and hits without hesitation. They use the name before she hears it: The Wraith.

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/4a0a2c23-2bb5-4b6e-a934-97f2ccf78607_Jordan-apocolipse-urban.png?v=62efd75772c274e27c330befe21c97af]]

— She doesn't correct them. The name is useful. The name doesn't grieve.

— Sometimes, very late, when she lets her guard down by half a degree: she talks. Quietly, to no one. Describes what she found that day. Notes something he would have made a terrible pun about. Then stops, and gets up and checks the perimeter again.

## Aidan's Thread

— The Grandiose Hotel. The kitchen is extraordinary. Commercial grade, largely intact. He stares at it for a long time.

— The weeks that follow are dark. He eats mechanically. He doesn't cook — he heats things. There is a difference and he feels it like an amputation.

— Then, on a Tuesday exactly like every other day, he breaks. Not dramatically — quietly, sitting on the kitchen floor at two in the morning. He gets up. He turns on the burner. He makes, out of almost nothing, something. It is not good. He eats every bite standing at the stove.

— The jokes come back slowly, one at a time, like a language being relearned. Each one a small flag planted in the ground that says: I am still here.

## The Radio

Aidan sits in the dark, the ham radio in front of him. He's been here twenty minutes and has said nothing. He picks up the handset.

SPEAKER:AIDAN (to the static)
> "This is Aidan. Aidan Lee. I'm at the old Grandiose Hotel on Fifth, by the convention center. I'm broadcasting mostly because it's better than not broadcasting."

SPEAKER:AIDAN
> "If anyone's listening — if she's listening, which I know she's not, but — the kitchen here is genuinely excellent. Five stars. The clientele has gone significantly downhill but the bones are good."

SPEAKER:AIDAN (his voice doing a great deal of work to stay controlled)
> "I keep the light on in the kitchen window at night. In case. That's all. Aidan out."

He sets the handset down. Static. He goes back to the kitchen.

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/43c3833a-87af-427b-ba43-ba40e111cef7_Aidan-are-you-there.png?v=72b76d4e842bc2b12c9e8bf3f09d4eec]]

## The Radio Shack

Jordan is scavenging. Moving through shelves automatically. She almost leaves.

Then, from the speaker — weak, breaking in and out, fighting a dying battery signal from somewhere across the city:

SPEAKER:AIDAN (radio — distant)
> "—anyone copy — this is Aidan Lee — broadcasting from the Grandiose Hotel on Fifth — if anyone can hear this — if Jordan can hear this — still here — still cooking—"

The signal breaks into nothing. Jordan has not moved.

She stands in the doorway of the radio shack and she doesn't breathe for a moment that is longer than a moment. Then she turns around, walks to the radio, and starts working the dials with careful, deliberate hands that are shaking badly enough to matter.

SPEAKER:JORDAN (barely audible)
> "Aidan. Come in."

Static. Her jaw tightens.

SPEAKER:JORDAN (louder)
> "Aidan. This is Jordan. Come in. Please."

The battery backup gives one last surge before it dies — and through the speaker, distorted by distance and failing equipment, comes a sound she has been carrying as a wound for almost a year.

His voice. Saying her name.

The armor she has been building, swing by swing, mile by mile — it doesn't break dramatically. It just stops being the most important thing in the room.

She slides down the wall to the floor, handset held against her chest, and lets herself, for the first time in a very long time, feel everything.

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/bce63b9a-cee8-43b9-a343-9a2ee871e43a_desperation.png?v=6d1085b6b1b432a0a1c50d69984c57b8]]

Two weeks later, Jordan moves through the city. But differently now. Not hollow — with direction. With heat. She has a walkie-talkie. Aidan's voice arrives through static at unexpected moments, doing exactly what it always did — making the world smaller and more survivable.

!! The story begins.`,
  },
  {
    id: "chapter1",
    number: "I",
    title: "Love and Ladles",
    subChapters: ["The Reunion"],
    content: `[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/2538661e-8a9f-489a-9500-8d56b30d4934_bat.png?v=62b60b91c4669b47fa304421ed95f893]]

## The Reunion

The world is gray and desolate. A rusty car sits on flat tires. A zombie lurches from a shattered storefront.

A baseball bat wrapped in barbed wire — HOME-RUN — smashes into its head with a sickening THWACK.

JORDAN stands over it, breathing heavily. She yanks her bat free. Her eyes are fixed ahead, determined.

SPEAKER:JORDAN
> "One step closer. Just a few more blocks."

A voice crackles over the walkie-talkie on her belt.

SPEAKER:AIDAN (V.O.)
> "J-Jordan? You there? Come in. Chef of the Apocalypse to his favorite batter... get it? Batter?"

A huge, relieved smile breaks across Jordan's face. She fumbles for the walkie.

SPEAKER:JORDAN
> "Aidan! I'm here! Your jokes are still terrible. Status?"

SPEAKER:AIDAN (V.O.)
> "Holed up in the old Grandiose Hotel. Kitchen's surprisingly well-stocked. I've been living like a king. A king who's surrounded by the undead and misses his queen. What's your ETA?"

SPEAKER:JORDAN
> "I saw the hotel. I'm close. Stay put. I'm coming for you."

SPEAKER:AIDAN (V.O.)
> "Roger that. I'll have a victory feast ready. Aidan out."

The radio clicks off. Jordan's grip on her bat tightens, her resolve hardening. She moves forward with renewed purpose.

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/b65371c2-394c-44ba-8625-d0e64fba0c94_thwack.png?v=690701bc52f8a764b48db57b5fde7f98]]

!! Jordan Reyes — Home-Run — One Step Closer.

## The Action Sequence

Jordan tries to sneak past a group of zombies, but a can clatters under her boot. Heads turn. Dozens of zombies shuffle towards her.

SPEAKER:JORDAN
> "So much for stealth."

She swings Home-Run, creating a path. But there are too many. She's getting surrounded. Suddenly, a window on the second floor of a building shatters.

SPEAKER:AIDAN (O.S.)
> "HEY! UGLIES! TRY THIS SPECIAL OF THE DAY!"

AIDAN leans out the window. He chucks a glass jar. It shatters on the ground, releasing a cloud of red powder. Zombies screech, clawing at their eyes.

SPEAKER:AIDAN
> "That's my ghost pepper and cayenne surprise! Adds a real kick!"

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/f544d13c-9c20-4e67-a41d-ef10b5d8614a_ghost-pepper-grenade.png?v=795901b3bfccca82e2db24cf73d6fee3]]

SPEAKER:JORDAN
> "AIDAN!"

He swings down on a rope made of tied-together bedsheets, landing gracefully beside her, skillet in hand. He bonks a zombie on the head. CLANG!

SPEAKER:AIDAN
> "Miss me?"

SPEAKER:JORDAN
> "Your timing is as dramatic as ever."

SPEAKER:AIDAN
> "A good chef knows when to make an entrance! Now, let's get back to my temporary kitchen. I've perfected a recipe for canned bean stew that will change your life!"

They fight back-to-back. Jordan's powerful swings are perfectly complemented by Aidan's chaotic, disabling attacks with flour bombs and his trusty skillet. They are a perfect, synchronized team.

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/67d531e0-7a7c-47c3-a29a-532d513f94d2_Jordan-and-Aidan-back-to-back.png?v=5eba20794eefd3405a54db91f080dcd8]]

## The Hotel Kitchen

The kitchen is barricaded. A single pot bubbles on a makeshift stove. Aidan is stirring it, humming. Jordan is catching her breath.

SPEAKER:JORDAN
> "You idiot. You could have been killed, coming out like that."

SPEAKER:AIDAN
> "And let my favorite taste-tester become a zombie appetizer? Not on my watch. A little risk is worth it for the main ingredient."

SPEAKER:JORDAN
> "And what's that?"

SPEAKER:AIDAN
> "You, Jordan. You're the main ingredient."

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/b1f6b38f-6e13-4753-8410-e64aeaa22622_Jordan-and-Aidan-Your-the-main-ingredient.png?v=3acc05cf18aaeb7c51c4a83c041298fb]]

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/3d55dbbe-1b1a-4a8a-9a7b-0535a6d1bd63_Jordan-and-Aidan-Kitchen.png?v=e974603114eea1fe28641d835c8233ed]]

The moment is soft. He ladles stew into a bowl and hands it to her. Their fingers touch. The chemistry is palpable.

SPEAKER:JORDAN
> "I was so scared I wouldn't find you."

SPEAKER:AIDAN
> "I knew you would. You fight for what you love. It's why I love you."

!! The Hotel Kitchen — "You're the Main Ingredient."

He says it so simply, so matter-of-factly. Jordan's eyes glisten. She leans in to kiss him, but a loud BANG against the hotel's main door makes them jump apart.

SPEAKER:JORDAN
> "They've found the main entrance. The barricade won't hold long."

SPEAKER:AIDAN
> "There's a service elevator to the roof. We can cross to the next building."

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/790478e0-d892-485f-af31-6b971b31c2dc_Jordan-and-Aidan-onto-the-roof.png?v=2fdfd07009d461c4ddc5c37fd2c790fb]]

## The Hotel Rooftop

They burst onto the roof. The city is dark, punctuated by occasional fires and moans. The only way out is a precarious plank leading to the next building.

SPEAKER:AIDAN
> "Oh, great. The Acrobat's Special."

As they start across, the hotel door to the roof splinters. Zombies pour out. Aidan goes first, testing the plank. Jordan covers the rear. Suddenly, the plank cracks. Aidan stumbles, dropping his skillet. It clatters down into the alley below.

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/7fa213ac-55de-4029-9a4a-c2fc5f242b39_Jordan-how-am-i-getting-out-of-this-one.png?v=f13eb4737bf718402d6f42d0778a0dc6]]

SPEAKER:JORDAN
> "Aidan!"

She grabs his arm, pulling him to safety on the other roof. The plank snaps completely, falling away. They're safe, but trapped, with the horde gathering on the opposite roof.

SPEAKER:AIDAN
> "Well. This is a pickle. And I'm all out of pickles."

SPEAKER:JORDAN
> "They're going to make it across."

SPEAKER:AIDAN
> "Okay. New plan. We jump."

He points to a dumpster in the alley below, filled with soft-looking trash bags.

SPEAKER:JORDAN
> "Are you crazy?!"

SPEAKER:AIDAN
> "It's that or become an all-you-can-eat buffet! I choose the messy landing!"

SPEAKER:JORDAN
> "Together?"

SPEAKER:AIDAN
> "Always."

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/1bdbc922-ad5a-42c6-a4a6-0210ae7d6f47_Jordan-and-Aidan-jump-for-it.png?v=1754300bd8b5c8625bac06ec7ece765e]]

They hold hands, back up for a running start, and leap just as the zombies reach their roof. SLAM! They hit the dumpster. It's painful, but they're alive.

!! Jump For It — Together or Not at All.

## The Garage at Dawn

They limp into a small, secure garage — a previous safe house. The sun is rising. They collapse against a wall, exhausted but safe. Aidan's hands are shaking.

SPEAKER:JORDAN
> "Hey. You okay?"

SPEAKER:AIDAN
> "Just... a little shaken. And not in a cocktail way."

SPEAKER:JORDAN
> "You were incredible."

SPEAKER:AIDAN
> "I was terrified. I just... I can't lose you, Jordan. This world... the cooking, the jokes... it's all just my way of pretending things are normal. But the only thing that keeps me going is you."

Jordan takes his face in her hands.

SPEAKER:JORDAN
> "You don't have to pretend with me. The jokes, the cooking... that's you. That's the boy I fell for. And you're not just my safe place, Aidan. You're my reason to fight."

!! "The Kiss in the Garage" — Certainty. Of Home.

She kisses him. It's not a kiss of desperation, but of certainty. Of home.

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/dd6366a2-fab0-4c4b-b349-5ba02b120b9c_Jordan-and-Aidan-heart-throb-kiss.png?v=8c1fb68a35b66745606a5eeb183319f9]]

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/3f14130b-c5a3-408c-b089-56eca7eb6346_Jordan-and-Aidan-the-kiss-in-the-garage.png?v=e13fac303ef91c3c2ed503066591603d]]

They pull apart, foreheads touching.

SPEAKER:AIDAN
> "So... what's for breakfast?"

Jordan laughs — a real, free sound.

SPEAKER:JORDAN
> "Whatever the chef prepares. But go easy on the ghost pepper this time."

SPEAKER:AIDAN
> "Your wish is my command. Omelets of hope coming right up!"

The morning light streams into the garage, illuminating them. Aidan is cooking, humming. Jordan watches him — not with the desperate determination of before, but with a quiet, deep love. They are together. They are home.

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/08bd6560-f664-492d-b48f-ddf537494c4a_Jordan-and-Aidan-the-dawn-back-in-the-garage.png?v=95ede3058d1fbd027bef2f54591911f9]]`,
  },
  {
    id: "chapter2",
    number: "II",
    title: "The Road to Somewhere",
    subChapters: ["The Garage", "The Road", "The Overpass"],
    content: `## The Garage

Aidan leans over the map Jordan found behind a rusted tool shelf, chewing a protein bar like a man savoring a five-course meal. In the cup holder nearby: a small glass jar of trail mix labeled in marker — "Optimism Blend (© Aidan Lee, Year 2 of the Apocalypse)."

SPEAKER:AIDAN
> "What are we looking at?"

SPEAKER:JORDAN
> "Aunt Maria's. About sixty miles north. Rural. Defensible. My mom was heading there when the city fell."

SPEAKER:AIDAN
> "Sixty miles. That's a solid road trip. Normally I'd suggest a playlist, but our speakers are currently a zombie horde."

SPEAKER:JORDAN
> "We need a vehicle."

He disappears behind a stack of old tires and returns dragging a dusty green tarp. He yanks it back with the flair of a magician revealing a trick. Underneath: a battered but intact pickup truck.

SPEAKER:JORDAN
> "You've had a truck this whole time."

SPEAKER:AIDAN
> "I had a project. There's a difference. I fixed the engine two weeks ago. Just hadn't gotten around to the tires yet."

SPEAKER:JORDAN
> "Aidan."

SPEAKER:AIDAN
> "I was scared to leave without you. If I drove off and something happened before I found you... I'd rather wait and eat cold beans for a month."

Jordan crosses to him. Takes his hand. Squeezes once.

SPEAKER:JORDAN
> "Let's fix the tires."

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/2613290e-ec57-47f5-9f4e-a1d6e401f9e3_Aidan-pulls-back-the-tarp.png?v=3adb051770901d47a64a0fdc97ca88fa]]

## The Road

The truck rumbles up a cracked highway. Aidan drives. Jordan rides shotgun, Home-Run braced against the door, watching the treeline. Jordan picks up the trail mix jar and reads the label. A laugh escapes before she can stop it.

SPEAKER:JORDAN
> "You labeled your trail mix."

SPEAKER:AIDAN
> "Branding is important even in societal collapse. Maybe especially then."

SPEAKER:JORDAN
> "You're genuinely unhinged."

SPEAKER:AIDAN
> "I prefer 'creatively adapted.'"

SPEAKER:JORDAN
> "When I found you on the radio... I hadn't laughed in four months. And then there was your voice making the worst possible pun about the undead, and I just... lost it."

SPEAKER:AIDAN
> "What kind of pun?"

SPEAKER:JORDAN
> "You called them your 'die-hard fans.'"

SPEAKER:AIDAN
> "That's a quality pun."

SPEAKER:JORDAN
> "It's a terrible pun. But I laughed so hard I knocked over everything on the shelf."

SPEAKER:AIDAN
> "I talked to you. Out loud. To the kitchen, basically. Just told you about my day. Pretended you were sitting across from me."

SPEAKER:JORDAN
> "What did I say? In your head."

SPEAKER:AIDAN
> "Mostly you told me the soup needed more salt. You were right, too."

## The Overpass

The road ahead is completely blocked. Thirty to forty zombies milling through an abandoned pileup. Aidan produces four glass jars sealed with wax.

SPEAKER:JORDAN
> "What is this?"

SPEAKER:AIDAN
> "I call it 'Diversion au Jus.' Fermented fish sauce, rotting compost, and — my secret ingredient — a hint of durian fruit. The smell carries about a quarter mile in a light breeze."

SPEAKER:JORDAN
> "You've been carrying around jars of the worst smell in human history."

SPEAKER:AIDAN
> "I've been curating a distraction arsenal. Yes."

SPEAKER:JORDAN
> "I love this man and I'm also afraid of him."

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/7634a178-1155-46aa-81f3-dfc12e8f825d_thirty-zombies-blocking-the-road.png?v=36ef3b0077e6cb3da235e14b92241b2a]]

!! "Diversion au Jus" — The Highway Overpass.

Aidan lobs two jars to the far side of the overpass. Every zombie pivots and lurches away, drawn by the overwhelming odor. Jordan clears the gap, dispatching the stragglers. They're through.`,
  },
  {
    id: "chapter3",
    number: "III",
    title: "The Millhaven Mercy",
    subChapters: ["Ruth", "Nina", "The Notebook"],
    content: `## Ruth

The truck rolls in slowly. A banner still strung between two lamp posts reads: MILLHAVEN FALL FAIR — OCTOBER 18TH. It will never happen.

A GUNSHOT cracks the air. The windshield gets a hole in the top right corner.

SPEAKER:VOICE (O.S.)
> "TURN AROUND! THIS TOWN IS CLAIMED!"

RUTH (50s) — hunting rifle, flannel shirt, and the demeanor of someone who hasn't slept properly in a year but has made peace with it — steps out from behind a burned car. She looks at the apron Aidan still has on. Then at the skillet in his hand. Then at his backpack, which clinks slightly from the jars inside.

SPEAKER:RUTH
> "Are you a... cook?"

SPEAKER:AIDAN
> "Culinary-school-trained survivor of the apocalypse, yes ma'am."

SPEAKER:RUTH
> "We've got seventeen people. Two of my people are sick. We've been eating cold canned goods for three weeks because our last person who knew how to use a stove got..."

She doesn't finish. We understand.

SPEAKER:RUTH
> "...we lost her."

Aidan and Jordan exchange a glance. He raises an eyebrow. She gives the smallest nod.

SPEAKER:AIDAN
> "Point me at your kitchen."

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/d4d0dd99-d076-4c29-82ab-669cf9c3e04b_first-contact-in-Millhaven.png?v=8f315131e52a06b7e023b7521442d6c6]]

## Nina

The smell of garlic and tomatoes drifts through the building. It is, in the gray ruin of everything else, extraordinary.

Survivors get up from their cots. Adults who haven't spoken to one another in days drift toward the kitchen doorway, drawn by something older than survival instinct — the smell of someone cooking for them. The smell of being cared for.

A LITTLE GIRL (7) — enormous eyes, knees pulled to her chest — approaches Jordan's station and holds up her bowl with both hands, very seriously.

SPEAKER:NINA
> "Is it real?"

Jordan crouches to her level.

SPEAKER:JORDAN
> "Yeah. It's real."

SPEAKER:NINA
> "It smells like my mom's kitchen."

SPEAKER:JORDAN
> "That's exactly what it's supposed to smell like."

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/1657225c-b745-480e-87ae-dd8769c5d1db_the-emotional-center-of-Millhaven.png?v=a31d9e5dca673ada563371e4ee1175a1]]

## The Notebook

SPEAKER:AIDAN (handing her a cup)
> "It's not good. I'm not going to pretend it's good."

SPEAKER:JORDAN
> "It's terrible."

SPEAKER:AIDAN
> "It's trying. That counts."

SPEAKER:AIDAN
> "When I was in the hotel. Alone. Talking to the ghost of you in my kitchen. The thing that kept me from going completely off the rails wasn't hope exactly — I still had people I was cooking for. Even if they weren't there. You. Your mom. Whoever was eventually going to walk through that door. It kept me tethered."

Jordan shifts, closes the small gap between them, and leans her head against his shoulder.

SPEAKER:JORDAN
> "We're leaving at first light. But I want to leave them something. A real recipe. Written down. So they can keep doing this when we're gone."

He digs into his jacket pocket and produces a small, battered notebook — the kind with the black-and-white marbled cover — and a stubby pencil. He presses them into her hands.

SPEAKER:AIDAN
> "I always kept this for recipes. Start a new page."

SPEAKER:JORDAN
> "Aidan. There are thirty recipes in here."

SPEAKER:AIDAN
> "Forty-two. I had a lot of evenings."

She starts writing — her handwriting big and plain and deliberate. Not elegant, but very clear. The kind of handwriting that means someone should be able to read this even if they're frightened.`,
  },
  {
    id: "chapter4",
    number: "IV",
    title: "What the Dark Holds",
    subChapters: ["Decker's Bridge", "The Weight of Guilt"],
    content: `## Decker's Bridge

The road ahead crosses a narrow bridge over a river gorge. Spray-painted in red letters across a metal barrier: TOLL.

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/f39038ff-92e9-4866-90bc-3c3493651216_the-toll.png?v=77cbebfb8bf7f52b33468e89ecd2cef1]]

DECKER — a large man with cold eyes and a compound bow — emerges with three others.

SPEAKER:DECKER (pleasant, which is somehow worse)
> "Nice truck. What are you carrying?"

SPEAKER:JORDAN (flat)
> "Nothing you want."

Aidan's hand moves to the gearshift. Jordan's hand moves to the door handle. They don't look at each other. They don't need to.

Aidan shifts into reverse. Jordan throws open the door, rolls out in one fluid motion, and comes up running. Home-Run swings in a controlled arc, sending Decker's bow off the bridge into the gorge. A seasoning grenade — cayenne and black pepper — detonates in a red cloud. Three of Decker's people reel back, coughing, eyes streaming.

Decker is bigger and angrier. He drives a fist into Jordan's shoulder, spinning her. Her back hits the railing. She grabs it. She hasn't gone over.

Decker's hand closes around Aidan's wrist before the skillet swing connects. Jordan plants her feet, shoves off the rail, and drives her elbow into the base of Decker's skull with the full momentum of her rebound. He goes down.

SPEAKER:JORDAN
> "Drive. Now."

She hooks Home-Run's barbed wire through the barrier's hinge bolt and wrenches. Metal tears. The barrier swings free. The truck roars through. Jordan sprints after it, vaults into the truck bed as it accelerates across the bridge.

## The Weight of Guilt

SPEAKER:JORDAN (quietly)
> "I let you get hurt at the supermarket. I still think about it. Every time something goes wrong, I think about it."

SPEAKER:AIDAN
> "Nothing went wrong at the supermarket. We both made it out. The fact that we came out different exits does not make it your fault."

SPEAKER:JORDAN (very quiet)
> "Sometimes I don't know who I am without the guilt. It's been so long."

SPEAKER:AIDAN
> "I know who you are without it."

SPEAKER:JORDAN
> "Yeah?"

SPEAKER:AIDAN
> "You're the girl who made sure a seven-year-old had a hot meal and a recipe to carry forward. You're the person who runs toward the thing everyone else runs from. You're the person who still asks how I'm doing in the middle of a zombie apocalypse."

She takes a breath. Lets it go.

SPEAKER:JORDAN
> "Keep driving."`,
  },
  {
    id: "chapter5",
    number: "V",
    title: "Home Is a Moving Target",
    subChapters: ["Elena", "The Dinner", "The Porch"],
    content: `## Elena

A fence. Reinforced, recently. New lumber mixed with old. Garden beds along the south-facing wall of a large stone farmhouse. A hand-painted sign on the gatepost:

REYES & FRIENDS.
YOU ARE WELCOME IF YOU COME IN PEACE.

Jordan sees the name and stops breathing. She's out of the truck before it fully parks — the run of someone afraid to believe what they're seeing.

A woman comes out of the farmhouse. ELENA REYES (50s) — small but with Jordan's exact posture, that compressed-spring readiness Jordan never noticed she inherited. She raises a hand to block the low sun. Her hand slowly drops.

SPEAKER:ELENA
> "JORDAN—"

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/5102d85f-9992-4e14-a970-56aa8b2fca6c_the-farmhouse-gate.png?v=0a96425be62b2bbe1a7b4b6748c11b87]]

They collide in the middle of the yard. Jordan's arms go around her mother so tight it looks structural. Elena's hands are in her daughter's hair, and neither of them makes any sound for a long moment — just holding on.

Aidan stands by the gate with his hands in his pockets and watches, and does not attempt to make a single joke, because some moments are bigger than that.

## The Dinner

Aidan has made: rosemary bread, roasted vegetables with chili oil, a dense bean stew with a complexity that should not be possible given the ingredients, and for a small boy of four — a bowl of sweet mashed squash that he regards with the seriousness of a tiny food critic before eating every bite.

Jordan sits between her mother and Aidan. She is quieter than everyone else, but it's not the old hollow quiet. She's listening, watching, taking inventory of something good. Under the table, Aidan's hand finds hers. No drama. Just his fingers lacing through hers, staying there.

Jordan squeezes back.

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/8815d72a-b1cf-46c4-9496-def0d00bfe84_Jordan--in-th-pasture.png?v=b5920dd0e3ae1701ec532546fbc2257a]]

## The Porch

The stars are extraordinary without light pollution. Jordan and Aidan sit on the porch steps. From inside, someone playing what sounds like a guitar badly but with great enthusiasm.

SPEAKER:AIDAN (leaning back on his hands)
> "What do we do now?"

It's a genuine question. The first time either of them has asked now what? without survival calculus driving the answer.

SPEAKER:JORDAN
> "Stay, for a while. Help them build whatever this is."

SPEAKER:AIDAN
> "The kitchen needs serious work. The pantry system is completely haphazard."

SPEAKER:JORDAN
> "The perimeter needs reinforcing. I can teach them to fight. Some of them. The ones who want to learn."

SPEAKER:AIDAN
> "There was a version of better that we didn't get. That little boy back at the table — he deserved to grow up with the old version of better."

SPEAKER:JORDAN
> "Yeah."

SPEAKER:AIDAN
> "But this is real. And it's ours. And I can make something good out of it."

SPEAKER:JORDAN
> "I know you can."

SPEAKER:AIDAN
> "I have a terrible pun about 'making something out of scraps' that I have been sitting on this entire conversation."

SPEAKER:JORDAN
> "I can see you holding it in."

SPEAKER:JORDAN (a beat)
> "Yeah. Yeah, I really do."

He grins. And as the guitar inside picks up something that sounds almost like it used to be a real song, Aidan Lee — chef, survivor, keeper of hope, terrible comedian — tells the worst pun Jordan Reyes has ever heard.

!! And she laughs, loud and free, in the dark.`,
  },
  {
    id: "epilogue",
    number: "EPI",
    title: "Epilogue: Three Months Later",
    subChapters: ["The Garden", "Welcome Back", "For Nina"],
    content: `## The Garden

The south fence has been reinforced. A small watchtower stands at the east corner, jury-rigged from scaffolding. Jordan is up on the walkway, drilling three people on what to do when a horde approaches. She moves with purpose but there's less edge to her now. She's teaching, not just surviving.

Below, Aidan's garden is a genuine garden. Herbs in clean rows, labeled with small wooden stakes in his neat handwriting. He's crouching in the dirt, transplanting seedlings. NINA sits a few feet away, watching him with the seriousness she gives everything.

SPEAKER:NINA
> "What is that one?"

SPEAKER:AIDAN
> "Basil. It smells amazing. Do you want to smell it?"

She leans forward. He holds the seedling close. She sniffs. Her face changes — just a degree, but it's there. Something unlocking.

SPEAKER:NINA (quietly)
> "My mom grew basil."

SPEAKER:AIDAN (gently)
> "Yeah? What did she make with it?"

SPEAKER:NINA
> "Sauce. For pasta."

SPEAKER:AIDAN
> "That's exactly right. That's the best thing to do with it. I'll tell you what — when this grows big enough, we'll make that sauce together. Deal?"

Nina considers this with great seriousness. Then she nods, once, definitively.

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/bb356b03-f7ca-456d-89ad-3c9b718bcc38_Aidan--Nina-single-character-peaceful.png?v=05bc6bb5933432142a1456125699560c]]

SPEAKER:NINA
> "Deal."

## Welcome Back

The light is gold and long. Jordan is coming in from the perimeter check, Home-Run on her shoulder but not gripped — resting there. A subtle thing. A difference only she knows. Aidan meets her at the garden gate with two cups of real tea from a sealed tin — the first genuinely good thing he's been able to offer in months. He's been saving it for the right moment.

SPEAKER:JORDAN (eyes closed, first sip)
> "Oh, that's good."

SPEAKER:AIDAN
> "I've been sitting on this tin for three weeks, waiting for the right moment."

SPEAKER:JORDAN
> "Like the truck."

SPEAKER:AIDAN
> "Like the truck. Except this time the timing is perfect."

SPEAKER:JORDAN
> "I don't have a word for what we're building here."

SPEAKER:AIDAN
> "It's people. It's a table. It's what you make when you stop running and start choosing. It's home. That's the word."

SPEAKER:JORDAN (trying it out like she hasn't used the word in a long time)
> "Home."

SPEAKER:AIDAN
> "Welcome back."

She reaches out and takes his hand. Together they walk into the light coming from the farmhouse windows, toward the sound of people who are, against all reasonable expectations, making something out of what's left.

## For Nina

Everyone is asleep. The farmhouse is quiet. Aidan is alone at the kitchen table, writing in a new notebook by candlelight.

He's writing a letter. The top of the page reads: "For Nina, when she's older. Things worth knowing."

!! Rule One: A full stomach does not solve a problem. But it makes every problem smaller. Start there.

!! Rule Two: The right person is the one who comes back for you. Make sure you're worth coming back to.

!! Rule Three: Make something good, every day. Even something small. It is the most defiant act available to us.

[[ILLUS:https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/f41118b8-84f7-4365-b7a5-9cfe1f8b0683_Rule-Three.png?v=95a07ae3b36a3ce5f714066da321a52a]]

He puts the pencil down. Jordan rests her chin on the top of his head.

SPEAKER:JORDAN (quietly)
> "That's a good one."

SPEAKER:AIDAN
> "I had a good teacher."

SPEAKER:JORDAN
> "The culinary school?"

SPEAKER:AIDAN
> "You, obviously. Keep up."

She laughs, soft and private, in the candlelit kitchen at the end of the world.

!! "If you still laugh, you're still winning."`,
  },
];
